import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';

import examApis from '../../../apis/exams';
import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import { StyledButton } from '../styled';
import TextInputWithIcon from '../../../components/TextInputWithIcon';

interface AddExamModalProps {
  open: boolean;
  onClose: () => void;
  academicYear: string;
  grade: string;
  division: string;
  termId: string;
  school: string;
  onExamAdded: () => void;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AddExamModal: React.FC<AddExamModalProps> = ({
  open,
  onClose,
  academicYear,
  grade,
  division,
  termId,
  school,
  onExamAdded,
  showToast
}) => {
  const [newExamName, setNewExamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setNewExamName('');
      setIsLoading(false);
    }
  }, [open]);

  const handleAddExam = async () => {
    setIsLoading(true);
    try {
      await examApis.createExam({
        academicYear,
        grade,
        division,
        termId,
        examName: newExamName,
        school: school
      });
      onExamAdded();
      onClose();
      setNewExamName('');
      showToast('Exam added successfully!', SnackbarSeverityType.success);
    } catch (error) {
      console.error('Error adding exam:', error);
      showToast('Failed to add exam.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAddButtonDisabled = !newExamName || isLoading;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add Exam'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='outlined'>
            Cancel
          </Button>
          <StyledButton onClick={handleAddExam} disabled={isAddButtonDisabled}>
            {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Add'}
          </StyledButton>
        </>
      }
    >
      <TextInputWithIcon
        label='Exam Name'
        placeholder='Enter Exam Name'
        required
        name='newExamName'
        value={newExamName}
        onChange={(e) => setNewExamName(e.target.value)}
        fullWidth
      />
    </DialogModal>
  );
};

export default AddExamModal;
